import { type PermissionString, parsePermissionString } from './permissions.ts'

export interface UserPermissions {
	platformPermissions: Array<{
		entity: string
		action: string
		access: string
	}>
	nightclubPermissions: Array<{
		entity: string
		action: string
		access: string
		nightclubId: string
	}>
	eventPermissions: Array<{
		entity: string
		action: string
		access: string
		nightclubId: string
		eventId: string
	}>
}

export interface PermissionContext {
	nightclubId?: string
	eventId?: string
}

export class PermissionChecker {
	private permissions: UserPermissions

	constructor(permissions: UserPermissions) {
		this.permissions = permissions
	}

	/**
	 * Check if user has a specific permission
	 */
	hasPermission(
		permission: PermissionString,
		context?: PermissionContext,
	): boolean {
		const { action, access, entity } = parsePermissionString(permission)

		// Check platform permissions first (these are global)
		const hasPlatformPermission = this.permissions.platformPermissions.some(
			(p) => p.entity === entity && p.action === action && p.access === access,
		)

		if (hasPlatformPermission) {
			return true
		}

		// Check nightclub permissions
		if (context?.nightclubId) {
			// Specific nightclub context provided - check for exact match
			const hasNightclubPermission = this.permissions.nightclubPermissions.some(
				(p) =>
					p.entity === entity &&
					p.action === action &&
					p.access === access &&
					p.nightclubId === context.nightclubId,
			)

			if (hasNightclubPermission) {
				return true
			}
		} else if (access === `own`) {
			// No specific nightclub context, but checking "own" permission
			// Check if user has this permission for ANY nightclub they're assigned to
			const hasAnyNightclubPermission =
				this.permissions.nightclubPermissions.some(
					(p) =>
						p.entity === entity && p.action === action && p.access === access,
				)

			if (hasAnyNightclubPermission) {
				return true
			}
		}

		// Check event permissions if context provided
		if (context?.eventId) {
			const hasEventPermission = this.permissions.eventPermissions.some(
				(p) =>
					p.entity === entity &&
					p.action === action &&
					p.access === access &&
					p.eventId === context.eventId,
			)

			if (hasEventPermission) {
				return true
			}
		}

		return false
	}

	/**
	 * Check if user has any of the provided permissions
	 */
	hasAnyPermission(
		permissions: PermissionString[],
		context?: PermissionContext,
	): boolean {
		return permissions.some((permission) =>
			this.hasPermission(permission, context),
		)
	}

	/**
	 * Check if user has all of the provided permissions
	 */
	hasAllPermissions(
		permissions: PermissionString[],
		context?: PermissionContext,
	): boolean {
		return permissions.every((permission) =>
			this.hasPermission(permission, context),
		)
	}

	/**
	 * Get all nightclub IDs where user has a specific permission
	 */
	getNightclubsWithPermission(permission: PermissionString): string[] {
		const { action, access, entity } = parsePermissionString(permission)

		// If user has platform 'any' permission, they have access to all nightclubs
		const hasPlatformAnyPermission = this.permissions.platformPermissions.some(
			(p) => p.entity === entity && p.action === action && p.access === `any`,
		)

		if (hasPlatformAnyPermission) {
			// Return a special marker to indicate "all nightclubs"
			// In practice, you'd need to fetch all nightclub IDs from somewhere
			return [`*`]
		}

		// Otherwise, return specific nightclub IDs where user has the permission
		return this.permissions.nightclubPermissions
			.filter(
				(p) =>
					p.entity === entity && p.action === action && p.access === access,
			)
			.map((p) => p.nightclubId)
	}

	/**
	 * Get all event IDs where user has a specific permission
	 */
	getEventsWithPermission(permission: PermissionString): string[] {
		const { action, access, entity } = parsePermissionString(permission)

		// Check platform permissions first
		const hasPlatformAnyPermission = this.permissions.platformPermissions.some(
			(p) => p.entity === entity && p.action === action && p.access === `any`,
		)

		if (hasPlatformAnyPermission) {
			return [`*`]
		}

		// Check nightclub permissions (which might grant access to all events in that nightclub)
		const nightclubsWithPermission = this.permissions.nightclubPermissions
			.filter(
				(p) =>
					p.entity === entity && p.action === action && p.access === access,
			)
			.map((p) => p.nightclubId)

		if (nightclubsWithPermission.length > 0) {
			// User has permission via nightclub role - would need event lookup
			return [`*`]
		}

		// Return specific event IDs
		return this.permissions.eventPermissions
			.filter(
				(p) =>
					p.entity === entity && p.action === action && p.access === access,
			)
			.map((p) => p.eventId)
	}

	/**
	 * Check if user can perform an action on a specific entity type
	 */
	canPerformAction(
		entity: string,
		action: string,
		context?: PermissionContext,
	): boolean {
		// Try both 'own' and 'any' access levels
		return (
			this.hasPermission(
				`${action}:own:${entity}` as PermissionString,
				context,
			) ||
			this.hasPermission(`${action}:any:${entity}` as PermissionString, context)
		)
	}

	/**
	 * Check if user is a platform administrator
	 */
	isPlatformAdmin(): boolean {
		return this.hasAnyPermission([
			`read:any:user`,
			`create:any:platform_invitation`,
			`read:any:platform_analytics`,
		])
	}

	/**
	 * Check if user can manage platform users
	 */
	canManagePlatformUsers(): boolean {
		return this.hasAnyPermission([
			`create:any:user`,
			`update:any:user`,
			`delete:any:user`,
			`create:any:platform_role`,
			`update:any:platform_role`,
		])
	}

	/**
	 * Check if user can delete users
	 */
	canDeleteUsers(): boolean {
		return this.hasPermission(`delete:any:user`)
	}

	/**
	 * Check if user can invite nightclub owners
	 */
	canInviteOwners(): boolean {
		return this.hasPermission(`create:any:platform_invitation`)
	}

	/**
	 * Check if user can access platform dashboard
	 */
	canAccessPlatformDashboard(): boolean {
		return this.hasAnyPermission([
			`read:any:platform_analytics`,
			`read:any:user`,
			`read:any:nightclub`,
		])
	}

	/**
	 * Check if user can view all nightclubs (platform oversight)
	 */
	canViewAllNightclubs(): boolean {
		return this.hasPermission(`read:any:nightclub`)
	}

	/**
	 * Check if user can view all events (platform oversight)
	 */
	canViewAllEvents(): boolean {
		return this.hasPermission(`read:any:event`)
	}

	/**
	 * Event-specific permission methods
	 */

	/**
	 * Check if user can manage event roles/team
	 */
	canManageEventTeam(eventId?: string, nightclubId?: string): boolean {
		return this.hasAnyPermission(
			[
				`create:own:event_role`,
				`create:any:event_role`,
				`update:own:event_role`,
				`update:any:event_role`,
				`delete:own:event_role`,
				`delete:any:event_role`,
			],
			{ eventId, nightclubId },
		)
	}

	/**
	 * Check if user can send event invitations
	 */
	canInviteEventMembers(eventId?: string, nightclubId?: string): boolean {
		return this.hasAnyPermission(
			[`create:own:event_invitation`, `create:any:event_invitation`],
			{ eventId, nightclubId },
		)
	}

	/**
	 * Check if user can manage event tickets
	 */
	canManageEventTickets(eventId?: string, nightclubId?: string): boolean {
		return this.hasAnyPermission(
			[
				`create:own:ticket_type`,
				`create:any:ticket_type`,
				`update:own:ticket_type`,
				`update:any:ticket_type`,
				`delete:own:ticket_type`,
				`delete:any:ticket_type`,
			],
			{ eventId, nightclubId },
		)
	}

	/**
	 * Check if user can view event analytics
	 */
	canViewEventAnalytics(eventId?: string, nightclubId?: string): boolean {
		return this.hasAnyPermission(
			[
				`read:own:purchase_session`,
				`read:any:purchase_session`,
				`read:own:ticket`,
				`read:any:ticket`,
			],
			{ eventId, nightclubId },
		)
	}

	/**
	 * Check if user can edit events
	 */
	canEditEvents(eventId?: string, nightclubId?: string): boolean {
		return this.hasAnyPermission([`update:own:event`, `update:any:event`], {
			eventId,
			nightclubId,
		})
	}

	/**
	 * Check if user can delete events
	 */
	canDeleteEvents(eventId?: string, nightclubId?: string): boolean {
		return this.hasAnyPermission([`delete:own:event`, `delete:any:event`], {
			eventId,
			nightclubId,
		})
	}

	/**
	 * Check if user can create events
	 */
	canCreateEvents(nightclubId?: string): boolean {
		return this.hasAnyPermission([`create:own:event`, `create:any:event`], {
			nightclubId,
		})
	}

	/**
	 * Check if user is an event organizer
	 */
	isEventOrganizer(eventId?: string, nightclubId?: string): boolean {
		return this.hasAnyPermission(
			[
				`update:own:event`,
				`update:any:event`,
				`delete:own:event`,
				`delete:any:event`,
				`create:own:event_role`,
				`create:any:event_role`,
			],
			{ eventId, nightclubId },
		)
	}

	/**
	 * Check if user is an event ticket manager
	 */
	isEventTicketManager(eventId?: string, nightclubId?: string): boolean {
		return this.hasAnyPermission(
			[
				`create:own:ticket_type`,
				`create:any:ticket_type`,
				`update:own:ticket_type`,
				`update:any:ticket_type`,
				`read:own:purchase_session`,
				`read:any:purchase_session`,
			],
			{ eventId, nightclubId },
		)
	}
}

/**
 * Transform user data from the root loader into UserPermissions format
 */
export function transformUserPermissions(user: {
	platformRoles: Array<{
		platformRole: {
			permissions: Array<{
				entity: string
				action: string
				access: string
			}>
		}
	}>
	nightclubRoles: Array<{
		nightclubId: string
		nightclubRole: {
			permissions: Array<{
				entity: string
				action: string
				access: string
			}>
		}
	}>
	eventRoles: Array<{
		nightclubId: string
		eventId: string
		eventRole: {
			permissions: Array<{
				entity: string
				action: string
				access: string
			}>
		}
	}>
}): UserPermissions {
	const platformPermissions = user.platformRoles.flatMap(
		(role) => role.platformRole.permissions,
	)

	const nightclubPermissions = user.nightclubRoles.flatMap((role) =>
		role.nightclubRole.permissions.map((p) => ({
			...p,
			nightclubId: role.nightclubId,
		})),
	)

	const eventPermissions = user.eventRoles.flatMap((role) =>
		role.eventRole.permissions.map((p) => ({
			...p,
			nightclubId: role.nightclubId,
			eventId: role.eventId,
		})),
	)

	return {
		platformPermissions,
		nightclubPermissions,
		eventPermissions,
	}
}
